/*
defines miscellaneous reusable functions
*/

import Link from '../model/link';
import ImageNode from '../model/imageNode';
import WikiNode from '../model/wikiNode';
import ConnectorNode from '../model/connectorNode';

/**
 * given information about an API, makes an ajax call using the given url, given request object, and headers
 *
 * the given api source must have the following info:
 * request
 * url
 * headers
 * @param {Object} apiSource information about the API to be called
 * @param {Object} apiResponse where to put the new response
 * @param {Function} [done] (optional) called when
 * @param {Function} [fail] (optional) called when
 * @param {Function} [always] (optional) called when
 * @return {JQueryXHR} the ajax object
 */
export function getNewDataFromAPI(apiSource, apiResponse, done = () => {}, fail = () => {}, always = () => {}) {
    // add a bit to the request data so we don't get old responses ("cache-busting")
    // _.assign(apiSource.request, {nocache: Date.now()});

    // make an ajax request using jquery to the given API
    let jqXHR = $.ajax({
        // location of api
        url: apiSource.url,
        // query parameters sent to the server
        data: apiSource.request,
        // meta information about the request
        headers: apiSource.headers,
        // xhrFields: {
        //    withCredentials: true
        // },
        // crossDomain: true,
        tryCount: 0,
        timeout: 10000,
        retryLimit: 1,
        cache: false,
    })
    // when this call returns, save the response from the server
    .done((newApiResponse) => {
        // integrates the response from the server with the node's data
        if (_.isEmpty(newApiResponse)) {
            console.error(`empty response from ${apiSource.url}`);
            fail();
        }

        _.assign(apiResponse, newApiResponse);

        // updates the view to reflect new data
        done();
        console.log(`successful response from ${apiSource.url}:`);
        console.log(apiSource);
        console.log(newApiResponse);
    })
    .fail(function(jqXHR, textStatus, errorThrown) {
        fail();
        console.log(`error from ${apiSource.url}:`);
        console.log(jqXHR);
        console.log(textStatus);
        console.log(errorThrown);
        this.tryCount++;
        if (this.tryCount <= this.retryLimit) {
            $.getJSON(this.url, this.data, done);
            return;
        }
        return;
    })
    .always(function(msg) {
        always();
        // console.log(`completed request to ${apiSource.url}:`);
        // console.log(msg);
    });

    console.log(`api request to ${apiSource.url} with data ${JSON.stringify(apiSource.request, null, 2)}`);
    return jqXHR;
}

/**
 * gets a formatted printout of the given metadata
 * @param {Object} metadata data object
 * @param {string} [apiName]
 * @param {string} [query]
 * @return {string} syntax highlighted metadata
 */
export function colorPrintMetaData(metadata, apiName, query) {
    let intro = apiName ? `${apiName} API, ${query ? query : 'random'} query:\n`: `local image \n`;
    let str = JSON.stringify(metadata, null, '\t');
    let res = intro + syntaxHighlight(str);
    res = res.replace(/{/gm, '');
    res = res.replace(/}/gm, '');
    res = res.replace(/\t/gm, '');
    res = res.replace(/",/gm, '');
    res = res.replace(/"/gm, '');
    console.log('metadata', res, metadata);
    return res;
}

/**
 * takes the given string and wraps span tags around the words so they can be clicked to create new nodes
 * @param {String} phrase 
 * @return {String}
 */
function clickableWords(phrase) {
    let isURL = (str) => /http/.test(str) || /base64/.test(str);
    if (isURL(phrase)) {
        return `<span class="word">${phrase}</span>`;
    } else {
        return phrase.replace(/[a-zA-Z]+/g, function(match, p1, p2, p3, offset, string) {
            return `<span class="word">${match}</span>`;
        });
    }
}

/**
 * adds highlighting tags to an object string
 * @param {string} json stringified object to be highlighted
 * @return {String}
 */
function syntaxHighlight(json) {
    json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g, function(match) {
        let cls = 'number';
        let str = match;
        if (/^"/.test(match)) {
            if (/:$/.test(match)) {
                cls = 'key';
            } else {
                cls = 'string';
                str = clickableWords(match);
            }
        } else if (/true|false/.test(match)) {
            cls = 'boolean';
        } else if (/null/.test(match)) {
            cls = 'null';
        }
        return `<span class="${cls}">${str}</span>`;
    });
}

/**
 * 
 * @param {Regex} regex 
 * @param {String} str 
 */
export function getMatches(regex, str) {
    let m;
    let matches = new Set();

    while ((m = regex.exec(str)) !== null) {
        // This is necessary to avoid infinite loops with zero-width matches
        if (m.index === regex.lastIndex) {
            regex.lastIndex++;
        }

        // The result can be accessed through the `m`-variable.
        // m.forEach((match, groupIndex) => {
        matches.add(m[1]);
        // });
    }
    return [...matches];
}

/**
 * remove the item in the given array which match the given ID
 * @param {Array} arr 
 * @param {string} id
 * @returns {boolean} returns whether the item was found and removed
 */
export function removeItem(arr, id, type) {
    let found = false;
    // console.log(`looking for ${id} in`, arr)
    arr.some((item, array_idx, array) => {
        if (item.id === id && !found) {
            array.splice(array_idx, 1);
            found = true;
            return found;
        }
        return false;
    });
    return found;
}

/**
 * 
 * @param {*} url 
 * @param {*} callback 
 */
export function getImageMeta(url, callback) {   
    let img = new Image();
    img.addEventListener('load', function() {
        callback(this.naturalWidth, this.naturalHeight);
    });
    img.src = url;
}

/**
 * get the text selected by the user.
 * 
 * not working currently
 */
export function getTextSelection() {
    let selectedRange = '';
    if (window.getSelection) {
        selectedRange = window.getSelection().toString();
        return selectedRange;
    } else {
        selectedRange = document.getSelection().toString();
    }
    return selectedRange;
}

/**
 * get the first item in the given array which matches the given ID
 * @param {Array} items 
 * @param {String} id 
 */
export function getItemById(items, id) {
    let foundItem = null;
    items.some((item) => {
        if (item.id == id) {
            foundItem = item;
            return true;
        }
        return false;
    });
    return foundItem;
}

/**
 * takes an array of apis and randomize the selection
 * return a random selection of apis
 * @param {Array} apis 
 */
export function randomizeSources(apis){
    let tempApi = apis.slice();
    let final = [];
    for (let i = 0; i<5; i++){
        let idx = Math.floor(Math.random() * Math.floor(tempApi.length));
        let removed = tempApi.splice(idx, 1);
        final.push(removed[0]);
    }
    return final;
}

/**
 * compresses the given image to match the given width, quality, and format
 * 
 * @param {Image} sourceImage image object to compress
 * @param {Number} maxWidth Integer
 * @param {Number} quality floating point number between 0 and 1
 * @param {String} outputFormat 'jpg' or 'png' or 'webp'
 * @return {Image} a compressed image object
 */
export function compressImage(sourceImage, maxWidth, quality, outputFormat = 'jpg') {

    let mimeType;
    if (outputFormat === 'png') {
        mimeType = 'image/png';
    } else if (outputFormat === 'webp') {
        mimeType = 'image/webp';
    } else {
        mimeType = 'image/jpeg';
    }

    let canvas = document.createElement('canvas');
    canvas.width = Math.min(sourceImage.naturalWidth, maxWidth);
    canvas.height = _.toInteger(canvas.width * sourceImage.naturalHeight / sourceImage.naturalWidth);
    const ctx = canvas.getContext('2d');
    ctx.drawImage(sourceImage, 0, 0, canvas.width, canvas.height);
    let newImageData = canvas.toDataURL(mimeType, quality);
    let resultImage = new Image();
    resultImage.src = newImageData;
    return resultImage;
}

/**
 * takes an svg transform string
 * (found in <rect transform="..."></rect> for example)
 * and turns it into a machine readable object,
 * @example
 * parseTransform('translate(3,4.5) scale(3.5)')
 * // =>
 * // {
 * //     translate: [3, 4.5],
 * //     scale: [3.5]
 * // }
 * @param {String} transformString the content of the svg transform
 * @return {Object} the parsed svg transform functions
 */
export function parseTransform(transformString) {
    if (!transformString) {
        return {
            translate: [0, 0],
            scale: [0],
        }
    }
    let parsedTransform = {};
    const transformFunctions = transformString.match(/(\w+\((\-?\d+\.?\d*e?\-?\d*,?)+\))+/g);
    for (let i = 0; i < transformFunctions.length; i++) {
        const transformFunction = transformFunctions[i].match(/[\w\.\-]+/g);
        const transformFunctionType = transformFunction[0];
        const transformFunctionArguments = transformFunction.slice(1);
        parsedTransform[transformFunctionType] = _.map(transformFunctionArguments, parseFloat);
    }
    return parsedTransform;
}

/**
 * given a string with html tags in it,
 * removes the html tags
 * 
 * @param {String} htmlString the string to strip of html tags
 * @returns {String} a clean string
 */
export function stripHTML(htmlString) {
   let tmp = document.createElement('div');
   tmp.innerHTML = htmlString;
   return tmp.textContent || tmp.innerText || '';
}

/**
 * replaces the links in the given html code
 * with buttons with the same content
 * @param {String} wikiText HTML code from wiktionary
 * @return {String} wiktionary HTML with links turned into buttons
 */
export function makeLinksIntoButtons(wikiText) {
    let newHTML = document.createElement('div');
    newHTML.innerHTML = wikiText;

    let iconSrc = '/imgs/wiki.png';
    $(newHTML).find('p').prepend(`<img src="${iconSrc}" id="node-icon"/>`);
    $(newHTML).find('a').replaceWith(function() {
        return '<button class=wikiLink>' + this.innerText + '</button>';
    });

    return newHTML.innerHTML;
}

/**
 * Add an icon to node pertaining to 
 * its source and purpose
 * @param {String} type
 * @param {String} text
 */
export function addIconToNode(type, text) {
    let newHTML = document.createElement('DIV');
    newHTML.innerHTML = `<span><p>${text}</p></span>`;
    let imgsrc = '/imgs/www.png';
    if (type === 'news') {
        imgsrc = '/imgs/news.png';
    } else if (type === 'wiktionary') {
        imgsrc = '/imgs/wiktionary.png';
    }
    $(newHTML).find('p').prepend(`<img src="${imgsrc}" id="node-icon"/>`);
    return newHTML.innerHTML;
}

/**
 * save a doc to cloud
 * @param {Object} json 
 */
export function saveDoc(json) {
    let cookie = getCookie("doc");
    if (cookie) {
        let doc = window.db.collection('graphs').doc(cookie)
        doc.get().then(docRef => {
            if (!docRef.exists){
                setCookie("doc", "NA")
            } else {
                doc.update(json).then(function(docRef2){
                    if (docRef2){
                        console.log(`Document updated at ${cookie}`)
                    }
                });
            }
        })
    } else {
        window.db.collection('graphs').add(json).then(function(docRef) {
            setCookie("doc", docRef.id);
            console.log(`New document saved at ${docRef.id}`)
        });
    }
}

/**
 * Import existing graph from database
 */
export function importDoc(){
    const cookie = getCookie("doc");
    if (cookie){
        return new Promise((resolve, reject) => {
            window.db.collection('graphs').doc(cookie).get()
                .then(doc => {
                    if (doc.data()){
                        resolve(importNodes(doc.data()));
                    }
                })
        })
        // return window.db.collection('graphs').doc(cookie).get()
        //          .then(function(doc){
        //              let data = doc.data();
        //              if (data){
        //                 return importNodes(data);
        //              }
        //          });
    } 
    return new Promise((resolve, reject)=>{
        resolve({nodes: [], links: [], view: {}});
    })
}

/**
 * import nodes from database, create node models here
 * 
 * @param {JSON} data 
 */
function importNodes(data){
    let nodes = [];
    let links = [];
    if (Object.keys(data).length > 0){
        data.model.nodes.forEach((node) => {
            let newNode;
            if (node.type === 'image'){
                newNode = new ImageNode(node.id, function(){}, {}, node.apiResponse, node.name, node.image, node.style, node.source);
            } else if (node.type === 'connector'){
                console.log('node', node);
                newNode = new ConnectorNode(node.id, node.text);
            } else if (node.type === 'wiki'){
                newNode = new WikiNode(node.id, function(){}, {}, node.apiResponse, node.name, node.style, node.source, node.query);
            }
            newNode.id = node.id;
            nodes.push(newNode);
        });
        data.model.links.forEach((link) => {
            let newLink = new Link(link.type, link.source, link.target, link.id, link.strength, link.distance);
            newLink.id = link.id;
            links.push(newLink);
        });
    }

    return {nodes, links, view: data.view}
}

export function setCookie(key, value) {
    document.cookie = encodeURIComponent(String(key)) + "=" + encodeURIComponent(String(value)) + ";path=/";
}

export function getCookie(name) {
    var cookieValue = null;
    if (document.cookie && document.cookie != '') {
        var cookies = document.cookie.split(';');
        for (var i = 0; i < cookies.length; i++) {
            var cookie = jQuery.trim(cookies[i]);
            // Does this cookie string begin with the name we want?
            if (cookie.substring(0, name.length + 1) == (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

/**
 * randomize index for api response
 */
export function randomizeIndex(){

}

/**
 * exports the above functions so they are available to other files
 */
export default {
    getNewDataFromAPI,
    colorPrintMetaData,
    getMatches,
    removeItem,
    getImageMeta,
    getTextSelection,
    getItemById,
    compressImage,
    parseTransform,
    stripHTML,
    makeLinksIntoButtons,
    addIconToNode,
    getCookie,
    setCookie,
    convertToDataURL,
    randomizeSources
};
